import React, { memo, useCallback, useEffect, useMemo, useRef } from 'react';
import Animated, { useAnimatedScrollHandler } from 'react-native-reanimated';
import { FlashList } from '@shopify/flash-list';
import Indicator from 'design-system/Indicator';
import { CardView } from 'design-system/CardView';
import Button, { HEIGHT } from 'design-system/Button';
import ListEmptyView from 'design-system/ListEmptyView';
import { rem, SCROLL_INDICATOR_INSETS } from 'design-system/values';
import useStyles from 'hooks/useStyles';
import { useMarginBottom } from 'hooks/useMarginBottom';
import createStyleSheets from 'utils/createStyleSheets';
import useAppFrameDimensions from 'hooks/useAppFrameDimensions';
import { useMainStackNavigation } from 'utils/types/navigationV6';
import { useAppDispatch, useAppSelector, useAppStore } from 'store/hooks';
import StockAlert from './StockAlert';
import { ALL_STOCKS_KEY } from '../constants';
import { isWeb } from '../../../constants';
import { selectIsInitialRequestingPendingStockAlerts, selectIsRequestingPendingStockAlerts, } from '../reducers/selectors';
import { getStockAlerts } from '../actions';
const AnimatedFlashList = Animated.createAnimatedComponent(FlashList);
const keyExtractor = (item) => item.alertId;
const PendingStockAlerts = ({ symbol, assetId, scrollY }) => {
    const navigation = useMainStackNavigation();
    const dispatch = useAppDispatch();
    const styles = useStyles(styleSet);
    const store = useAppStore();
    const shouldLoadSecondPage = useRef(false);
    const { paddingHorizontalStyle } = useAppFrameDimensions(isWeb);
    const paddingBottom = useMarginBottom('paddingBottom', HEIGHT + rem(16), paddingHorizontalStyle, styles.flashList);
    const loading = useAppSelector((store) => selectIsRequestingPendingStockAlerts(store, assetId));
    const initialRequesting = useAppSelector((store) => selectIsInitialRequestingPendingStockAlerts(store, assetId));
    const pendingStockAlerts = useAppSelector((state) => state.unpersistedInvestReducer.pendingStockAlerts?.[assetId || ALL_STOCKS_KEY]);
    useEffect(() => {
        if (!initialRequesting) {
            dispatch(getStockAlerts(assetId));
        }
    }, [assetId]);
    const loadMoreData = useCallback(() => {
        const token = !assetId
            ? store.getState().unpersistedInvestReducer.allPendingStockAlertsPaginationToken
            : store.getState().unpersistedInvestReducer.pendingStockAlertsPaginationToken;
        if (token) {
            dispatch(getStockAlerts(assetId, token));
        }
    }, [assetId]);
    const onLoadNextPage = useCallback(() => {
        if (loading) {
            shouldLoadSecondPage.current = true;
        }
        else {
            loadMoreData();
        }
    }, [loadMoreData, loading]);
    const scrollHandler = useAnimatedScrollHandler((handle) => {
        // eslint-disable-next-line no-param-reassign
        scrollY.value = handle.contentOffset.y;
    });
    const handleSearch = useCallback(() => {
        navigation.navigateDeprecated('InvestSearchStock', { stockAlertFlow: true });
    }, []);
    const ListEmptyComponent = useMemo(() => {
        if (initialRequesting) {
            return (<CardView noMarginTop noMarginBottom style={styles.loaderCard}>
          <Indicator dark/>
        </CardView>);
        }
        return (<ListEmptyView onCard title="Nothing to show" subTitle={!assetId && !symbol ? 'Choose a stock to create an alert' : ''}>
        {!assetId && !symbol && <Button hug medium brandReversed title="Search for stocks" onPress={handleSearch}/>}
      </ListEmptyView>);
    }, [assetId, handleSearch, initialRequesting, styles.loaderCard, symbol]);
    const itemsLength = pendingStockAlerts?.length || 0;
    const renderItem = useCallback(({ item, index }) => (<StockAlert index={index} key={item.alertId} style={styles.card} symbol={item.symbol} alertId={item.alertId} assetId={item.assetId} itemsLength={itemsLength} alertPriceValue={item.targetPrice.value} alertPriceCurrency={item.targetPrice.currency}/>), [itemsLength, styles.card]);
    return (<AnimatedFlashList extraData={styles.card} renderItem={renderItem} scrollEventThrottle={16} onScroll={scrollHandler} data={pendingStockAlerts} keyExtractor={keyExtractor} onEndReached={onLoadNextPage} keyboardDismissMode="on-drag" keyboardShouldPersistTaps="handled" contentContainerStyle={paddingBottom} ListEmptyComponent={ListEmptyComponent} scrollIndicatorInsets={SCROLL_INDICATOR_INSETS}/>);
};
export default memo(PendingStockAlerts);
const styleSet = createStyleSheets((colors) => ({
    flashList: {
        paddingTop: rem(16),
    },
    card: {
        backgroundColor: colors.cards.onDark,
    },
    loaderCard: {
        padding: rem(156),
    },
}));
